import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Calendar, 
  Users, 
  DollarSign,
  Activity,
  Download,
  RefreshCw,
  Filter,
  Eye,
  PieChart,
  LineChart
} from 'lucide-react';
import { Card, Button, Select, Badge } from '../../components/ui';
import AdminSidebar from '../../components/layout/AdminSidebar';
import { appointmentsAPI, patientsAPI, doctorsAPI, servicesAPI, realTimeUpdates } from '../../services/api';
import { formatCurrency, formatDate } from '../../utils';

// Simple chart components for visualization
const SimpleBarChart: React.FC<{ data: Array<{ name: string; value: number }> }> = ({ data }) => {
  const maxValue = Math.max(...data.map(d => d.value));
  
  return (
    <div className="space-y-2">
      {data.map((item, index) => (
        <div key={index} className="flex items-center space-x-3">
          <span className="text-sm font-medium text-gray-700 w-20 truncate">{item.name}</span>
          <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
            <div 
              className="bg-blue-600 h-4 rounded-full transition-all duration-500"
              style={{ width: `${(item.value / maxValue) * 100}%` }}
            />
            <span className="absolute right-2 top-0 text-xs text-white font-medium leading-4">
              {item.value}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

const SimplePieChart: React.FC<{ data: Array<{ name: string; value: number; color: string }> }> = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  return (
    <div className="flex items-center justify-center">
      <div className="relative w-48 h-48">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          {data.map((item, index) => {
            const percentage = (item.value / total) * 100;
            const strokeDasharray = `${percentage * 2.51} 251`;
            const previousPercentages = data.slice(0, index).reduce((sum, prev) => sum + (prev.value / total) * 100, 0);
            const strokeDashoffset = -previousPercentages * 2.51;
            
            return (
              <circle
                key={index}
                cx="50"
                cy="50"
                r="40"
                fill="none"
                stroke={item.color}
                strokeWidth="8"
                strokeDasharray={strokeDasharray}
                strokeDashoffset={strokeDashoffset}
                className="transition-all duration-500"
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{total}</p>
            <p className="text-sm text-gray-600">Total</p>
          </div>
        </div>
      </div>
      <div className="ml-6 space-y-2">
        {data.map((item, index) => (
          <div key={index} className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: item.color }} />
            <span className="text-sm text-gray-700">{item.name}: {item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminReports: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [patients, setPatients] = useState<any[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [timeRange, setTimeRange] = useState('month');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load all data from API
  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [appointmentsResult, patientsResult, doctorsResult, servicesResult] = await Promise.all([
        appointmentsAPI.getAll(),
        patientsAPI.getAll(),
        doctorsAPI.getAll(),
        servicesAPI.getAll()
      ]);

      if (appointmentsResult.success && appointmentsResult.data) {
        setAppointments(appointmentsResult.data);
      }

      if (patientsResult.success && patientsResult.data) {
        setPatients(patientsResult.data);
      }

      if (doctorsResult.success && doctorsResult.data) {
        setDoctors(doctorsResult.data);
      }

      if (servicesResult.success && servicesResult.data) {
        setServices(servicesResult.data);
      }
    } catch (err) {
      setError('Network error while loading report data');
      console.error('Error loading report data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const timeRangeOptions = [
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  // Calculate metrics based on time range
  const getFilteredData = () => {
    const now = new Date();
    let startDate = new Date();
    
    switch (timeRange) {
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
    }

    return {
      appointments: appointments.filter(apt => new Date(apt.date) >= startDate),
      patients: patients.filter(p => new Date(p.createdAt) >= startDate)
    };
  };

  const { appointments: filteredAppointments, patients: filteredPatients } = getFilteredData();

  // Revenue calculation
  const totalRevenue = filteredAppointments
    .filter(apt => apt.status === 'completed')
    .reduce((sum, apt) => {
      const service = services.find(s => s.id === apt.serviceId);
      return sum + (service?.price || 150);
    }, 0);

  // Appointment status distribution
  const appointmentStatusData = [
    { 
      name: 'Completed', 
      value: filteredAppointments.filter(apt => apt.status === 'completed').length,
      color: '#10B981'
    },
    { 
      name: 'Scheduled', 
      value: filteredAppointments.filter(apt => apt.status === 'scheduled').length,
      color: '#F59E0B'
    },
    { 
      name: 'Confirmed', 
      value: filteredAppointments.filter(apt => apt.status === 'confirmed').length,
      color: '#3B82F6'
    },
    { 
      name: 'Cancelled', 
      value: filteredAppointments.filter(apt => apt.status === 'cancelled').length,
      color: '#EF4444'
    }
  ];

  // Popular services
  const serviceStats = services.map(service => ({
    name: service.name,
    value: filteredAppointments.filter(apt => apt.serviceId === service.id).length,
    revenue: filteredAppointments
      .filter(apt => apt.serviceId === service.id && apt.status === 'completed')
      .length * service.price
  })).sort((a, b) => b.value - a.value).slice(0, 5);

  // Doctor performance
  const doctorStats = doctors.map(doctor => ({
    name: doctor.name.split(' ')[1], // Last name for brevity
    value: filteredAppointments.filter(apt => apt.doctorId === doctor.id).length,
    completed: filteredAppointments.filter(apt => apt.doctorId === doctor.id && apt.status === 'completed').length
  })).sort((a, b) => b.value - a.value);

  // Patient demographics
  const genderDistribution = [
    { 
      name: 'Female', 
      value: filteredPatients.filter(p => p.gender === 'female').length,
      color: '#EC4899'
    },
    { 
      name: 'Male', 
      value: filteredPatients.filter(p => p.gender === 'male').length,
      color: '#3B82F6'
    },
    { 
      name: 'Other', 
      value: filteredPatients.filter(p => p.gender === 'other').length,
      color: '#8B5CF6'
    }
  ];

  // Daily appointments (last 7 days)
  const dailyAppointments = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    const dayAppointments = appointments.filter(apt => 
      new Date(apt.date).toDateString() === date.toDateString()
    );
    
    return {
      name: date.toLocaleDateString('en', { weekday: 'short' }),
      value: dayAppointments.length
    };
  });

  const kpiCards = [
    {
      title: 'Total Revenue',
      value: formatCurrency(totalRevenue),
      change: '+12.5%',
      changeType: 'positive',
      icon: DollarSign,
      color: 'bg-green-500'
    },
    {
      title: 'Total Appointments',
      value: filteredAppointments.length,
      change: '+8.2%',
      changeType: 'positive',
      icon: Calendar,
      color: 'bg-blue-500'
    },
    {
      title: 'New Patients',
      value: filteredPatients.length,
      change: '+15.1%',
      changeType: 'positive',
      icon: Users,
      color: 'bg-purple-500'
    },
    {
      title: 'Completion Rate',
      value: `${Math.round((appointmentStatusData[0]?.value / filteredAppointments.length) * 100 || 0)}%`,
      change: '+2.3%',
      changeType: 'positive',
      icon: Activity,
      color: 'bg-orange-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <AdminSidebar isOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
      
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-72' : 'ml-20'}`}>
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Analytics & Reports</h1>
                <p className="text-gray-600">Comprehensive clinic performance metrics</p>
              </div>
              
              <div className="flex items-center space-x-4">
                <Select
                  options={timeRangeOptions}
                  value={timeRange}
                  onChange={setTimeRange}
                  className="min-w-32"
                />
                <Button variant="outline" onClick={() => setLoading(true)}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button>
                  <Download className="w-4 h-4 mr-2" />
                  Export Report
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="p-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {kpiCards.map((kpi, index) => (
              <Card key={index} className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{kpi.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{kpi.value}</p>
                    <div className="flex items-center mt-1">
                      {kpi.changeType === 'positive' ? (
                        <TrendingUp className="w-4 h-4 mr-1 text-green-600" />
                      ) : (
                        <TrendingDown className="w-4 h-4 mr-1 text-red-600" />
                      )}
                      <span className={`text-sm ${
                        kpi.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {kpi.change}
                      </span>
                    </div>
                  </div>
                  <div className={`w-12 h-12 ${kpi.color} rounded-lg flex items-center justify-center`}>
                    <kpi.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Daily Appointments Chart */}
            <Card>
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Daily Appointments (Last 7 Days)
                  </h3>
                  <LineChart className="w-5 h-5 text-gray-400" />
                </div>
              </div>
              <div className="p-6">
                <SimpleBarChart data={dailyAppointments} />
              </div>
            </Card>

            {/* Appointment Status Distribution */}
            <Card>
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Appointment Status Distribution
                  </h3>
                  <PieChart className="w-5 h-5 text-gray-400" />
                </div>
              </div>
              <div className="p-6">
                <SimplePieChart data={appointmentStatusData} />
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Popular Services */}
            <Card>
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Popular Services
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {serviceStats.map((service, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{service.name}</p>
                        <p className="text-sm text-gray-600">
                          {service.value} appointments • {formatCurrency(service.revenue)}
                        </p>
                      </div>
                      <Badge variant="primary">
                        #{index + 1}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Doctor Performance */}
            <Card>
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Doctor Performance
                </h3>
              </div>
              <div className="p-6">
                <SimpleBarChart data={doctorStats.slice(0, 4)} />
              </div>
            </Card>

            {/* Patient Demographics */}
            <Card>
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Patient Demographics
                </h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Gender Distribution</h4>
                    <div className="space-y-2">
                      {genderDistribution.map((item, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-sm text-gray-700">{item.name}</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Average Age</span>
                      <span className="text-sm font-medium text-gray-900">
                        {Math.round(
                          filteredPatients.reduce((sum, p) => {
                            const age = new Date().getFullYear() - new Date(p.dateOfBirth).getFullYear();
                            return sum + age;
                          }, 0) / filteredPatients.length || 0
                        )} years
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Patients</span>
                      <span className="text-sm font-medium text-gray-900">{filteredPatients.length}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Monthly Revenue Trend */}
          <Card className="mt-8">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Revenue Trends
                </h3>
                <div className="flex items-center space-x-2">
                  <Button variant="outline" size="sm">
                    <Filter className="w-4 h-4 mr-1" />
                    Filter
                  </Button>
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4 mr-1" />
                    Details
                  </Button>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
                  <p className="text-sm text-gray-600">Total Revenue</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(Math.round(totalRevenue / filteredAppointments.filter(apt => apt.status === 'completed').length || 0))}
                  </p>
                  <p className="text-sm text-gray-600">Average per Visit</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">
                    {Math.round((appointmentStatusData[0]?.value / filteredAppointments.length) * 100 || 0)}%
                  </p>
                  <p className="text-sm text-gray-600">Completion Rate</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminReports;